import React, { useState, useMemo, useRef, useCallback } from 'react';
import classNames from 'classnames';
import { focusManager } from '@accedo/vdkweb-navigation';
import { composeItemId, getItemsProps } from './swimlaneUtils';
import VisualWindow from './VisualWindow';
import styles from './swimlane.module.scss';
import useLatest from '../../hooks/useLatest';
import { getRelativeVWPixel } from '../../utils/relativePxValues';
import useUpdateEffect from '../../hooks/useUpdateEffect';
import FocusDiv from '../focus-div/FocusDiv';
import { verticalSmoothScroll } from '../../utils/pageUtils';
import { FocusNavigationArrow } from '../navigation-arrow/NavigationArrow';
const MIN_SCROLL = 0;

type Props<ItemDataType, ItemExtraData> = {
  pageId?: string;
  nav: XDKNav;
  data: ItemDataType[];
  itemExtraData?: ItemExtraData;
  hideOverflow?: boolean;
  resetAxisOnItemsChanged?: boolean;
  className?: string;
  headerClassName?: string;
  itemWrapperClassName?: string;
  extraPush?: number;
  category?: string;
  fixedFocus?: boolean;
  displayText?: string;
  shouldScroll?: boolean;
  animation?: boolean;
  component: React.ComponentType<any>;
  onFocus?: (id: string) => void;
  customOnClick?: (data: any) => void;
  onSwimlaneReady?: () => void;
  getItemData?: (item: ItemDataType) => ItemDataType;
  slideCount?: number;
};

const Swimlane = <ItemDataType, ItemExtraData>({
  pageId,
  nav,
  data = [],
  itemExtraData,
  hideOverflow = false,
  resetAxisOnItemsChanged = false,
  className = '',
  headerClassName = '',
  itemWrapperClassName = '',
  extraPush = 0,
  category = '',
  fixedFocus = false,
  displayText = '',
  shouldScroll = true,
  animation,
  component,
  onFocus,
  customOnClick,
  onSwimlaneReady,
  getItemData,
  slideCount = 2,
}: Props<ItemDataType, ItemExtraData>) => {
  if (!nav) {
    return null;
  }
  const [axis, setAxis] = useState(0);
  const latestAxisRef = useLatest(axis);
  const latestOnFocusRef = useLatest(onFocus);
  const shouldResetAxisRef = useLatest(resetAxisOnItemsChanged);
  const currentIndexRef = useRef(0);
  const [focusTrail, setFocusTrail] = useState<any>({
    lastFocusedEl: nav.id,
    curFocusedEl: '',
  });
  const [isSwimlaneFocused, setIsSwimlaneFocused] = useState(false);
  // const [currentPos, setCurrentPos] = useState({
  //   index: 0,
  //   position: 0,
  //   newPosition: 0,
  // });
  const [, setUpdate] = useState(0);
  const currentPos = useRef({
    index: 0,
    position: 0,
    newPosition: 0,
  });
  const onItemBlur = useCallback(
    currentId => {
      if (focusTrail.lastFocusedEl !== nav.id) {
        return;
      }

      setFocusTrail({
        ...focusTrail,
        lastFocusedEl: currentId,
        curFocusedEl: focusManager.getCurrentFocus(),
      });
    },
    [focusTrail],
  );

  const restoreFocusTrail = useCallback((direction: string) => {
    setFocusTrail({
      ...focusTrail,
      lastFocusedEl: nav.id,
      curFocusedEl: '',
    });

    if (direction === 'nextup' && nav.nextup) {
      setIsSwimlaneFocused(false);
    }

    if (direction === 'nextdown' && nav.nextdown) {
      setIsSwimlaneFocused(false);
    }
  }, []);

  const onItemFocus = useCallback(
    index => {
      (document.activeElement as any).blur();
      const wrapper = document.getElementById(nav.id);
      const currentItem = document.getElementById(composeItemId(nav.id, index));
      const currentAxis = latestAxisRef.current;

      if (!currentItem || !wrapper) {
        return;
      }

      if (latestOnFocusRef.current) {
        latestOnFocusRef.current(composeItemId(nav.id, index));
      }

      currentIndexRef.current = index;

      let newAxis = currentAxis;

      if (fixedFocus) {
        newAxis = getFixedFocusScroll(currentItem);
      } else {
        newAxis = getFloatingFocusScroll(currentAxis, currentItem, wrapper);
      }

      if (currentAxis !== newAxis) {
        setAxis(Math.min(newAxis, MIN_SCROLL));
      }
    },
    [extraPush],
  );

  const getFixedFocusScroll = (currentItem: HTMLElement): number => {
    return currentItem.offsetLeft * -1;
  };

  const getFloatingFocusScroll = (
    currentAxis: number,
    currentItem: HTMLElement | null,
    wrapper: HTMLElement | null,
  ): number => {
    if (!currentItem || !wrapper) {
      return currentAxis;
    }

    const swimlaneWidth = wrapper.offsetWidth;
    const swimlaneLeft = wrapper.offsetLeft;
    const itemLeft = currentItem.offsetLeft;
    const itemWidth = currentItem.offsetWidth;
    // If no extra push is provided, it will default to an extra push of itemWidth / 3
    const extraAxisPush = getRelativeVWPixel(extraPush) || itemWidth / 3;

    const itemRight = itemLeft + itemWidth;
    let newAxis = currentAxis;

    if (itemRight + currentAxis > swimlaneWidth) {
      // scroll right
      newAxis = swimlaneWidth - (itemRight + extraAxisPush);
    } else if (itemLeft + swimlaneLeft + currentAxis < swimlaneLeft) {
      // scroll left
      newAxis = -(itemLeft - extraAxisPush);
    }

    return newAxis;
  };

  const onWrapperFocus = useCallback(() => {
    if (shouldScroll) {
      verticalSmoothScroll(nav.id);
    }
    focusManager.changeFocus(composeItemId(nav.id, currentIndexRef.current));
    setIsSwimlaneFocused(true);
  }, [nav.id]);

  useUpdateEffect(() => {
    if (shouldResetAxisRef.current && latestAxisRef.current !== 0) {
      currentIndexRef.current = 0;
      setAxis(MIN_SCROLL);
    }
  }, [data]);

  const page = React.useCallback(
    direction => {
      (document.activeElement as any).blur();
      if (direction === 1) {
        const newIndex = currentPos.current.index + 1;
        const newCalculatedPos = slideCount + slideCount * newIndex;
        const newPos =
          newCalculatedPos > data.length - 1
            ? data.length - 1
            : newCalculatedPos - 1;
        const mod = Math.floor(newPos / slideCount);
        currentPos.current = {
          index: newIndex,
          newPosition: newPos,
          position: mod === 0 ? newPos : newPos + mod,
        };
        focusManager.changeFocus(composeItemId(nav.id, newPos));
      } else if (direction === -1) {
        const newIndex = currentPos.current.index - 1;
        const newCalculatedPos = slideCount * newIndex;
        const newPos = newCalculatedPos <= 0 ? 0 : newCalculatedPos;
        const mod = newPos % slideCount;
        currentPos.current = {
          index: newIndex <= 0 ? 0 : newIndex,
          position: mod === 0 ? newPos : 0,
          newPosition: newPos,
        };
        focusManager.changeFocus(composeItemId(nav.id, newPos));
      }
      focusManager.changeFocus('');
      setUpdate(Math.random());
    },
    [currentPos],
  );

  const items = useMemo(() => {
    return getItemsProps<ItemDataType, ItemExtraData>({
      data: data,
      getItemData,
      component,
      category,
      itemWrapperClassName,
      title: displayText,
      customOnClick,
      onItemFocus,
      onItemBlur,
      restoreFocusTrail,
      itemExtraData,
      swimlaneId: nav.id,
      pageId,
    });
  }, [data]);
  const isDataLengthSufficient = data.length > slideCount;
  return (
    <FocusDiv
      nav={nav}
      onFocus={onWrapperFocus}
      className={classNames(styles.wrapper, className, {
        [styles.noOverflow]: hideOverflow,
      })}
      style={{
        position: 'relative',
        zIndex: 1,
      }}
      dataName="carousel"
    >
      {displayText && (
        <div
          className={classNames(headerClassName || styles.header, {
            [styles.headerFocused]: isSwimlaneFocused,
          })}
        >
          {displayText}
        </div>
      )}
      <div
        className={classNames(styles.innerWrapper, {
          [styles.animation]: animation,
          [styles.noAnimation]: !animation,
        })}
        style={{
          transform: `translateX(${axis}px)`,
        }}
      >
        {items.length && (
          <VisualWindow
            pageId={pageId}
            swimlaneId={nav.id}
            items={items}
            onSwimlaneReady={onSwimlaneReady}
          />
        )}
      </div>
      {isDataLengthSufficient && (
        <>
          {currentPos.current.newPosition > 0 && (
            <FocusNavigationArrow onClick={() => page(-1)} nav={nav} left />
          )}
          {currentPos.current.newPosition < data.length - 1 && (
            <FocusNavigationArrow onClick={() => page(1)} nav={nav} right />
          )}
        </>
      )}
    </FocusDiv>
  );
};

export default Swimlane;
